import { promises as fs } from "node:fs";
// Scaffold the .claude/ directory tree inside a project.
// v3: workspace KHÔNG git, 1 flow init duy nhất (bỏ internal/client/library modes).
import { join } from "node:path";
import { CLAUDE_GITIGNORE_CONTENT } from "./claude-gitignore-required-patterns.js";
import { ensureDir, pathExists, writeTextAtomic } from "./filesystem-helpers.js";
import { renderTemplateByName } from "./template-bundle-loader.js";
import { withSettingsFileLock } from "./with-settings-file-lock.js";

// Top-level paths Avatar will create or overwrite during init.
// init.ts imports this for conflict detection so the list stays in sync.
// v3: workspace KHÔNG git → bỏ .gitmodules (không còn submodule).
export const AVATAR_MANAGED_PATHS = [".claude", "CLAUDE.md"] as const;

// Rename `path` to `{path}.avatar-backup-{ISO timestamp}` if it exists.
// Returns the backup path if created, null if source didn't exist.
// Caller logs the backup so the user knows where their original file went.
//
// Collision handling: if the timestamped backup name already exists (two
// backups within the same millisecond, or pre-existing leftover from prior
// run), appends `-{counter}` until a free name is found. Prevents silent
// overwrite of a previous backup.
export async function backupIfExists(path: string): Promise<string | null> {
  if (!(await pathExists(path))) return null;
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  const basePath = `${path}.avatar-backup-${ts}`;
  let backupPath = basePath;
  let counter = 1;
  while (await pathExists(backupPath)) {
    backupPath = `${basePath}-${counter}`;
    counter++;
    if (counter > 5) {
      throw new Error(`Could not find free backup name for ${path}`);
    }
  }
  await fs.rename(path, backupPath);
  return backupPath;
}

// Backup-aware atomic write. Used by all scaffolder write functions so user's
// pre-existing files in `.claude/` are preserved when Avatar re-scaffolds.
// Returns the backup path if a backup was created, null otherwise.
async function writeWithBackup(
  path: string,
  content: string,
  mode?: number,
): Promise<string | null> {
  const backup = await backupIfExists(path);
  await writeTextAtomic(path, content, mode);
  return backup;
}

// Subdirectories Avatar creates inside .claude/ on init. `pack/` is added
// separately (tải tarball qua installTeamPack) — không liệt kê ở đây.
// v1.3.3: Bỏ "project" subdir vì CLAUDE.md template không còn reference các knowledge file
// trong đó (team-ai-pack/knowledge auto-scan chưa implement). Khi implement xong sẽ
// add lại + writeProjectKnowledgeFiles.
const CLAUDE_SUBDIRS = ["state", "_pending", "_backup"] as const;

export interface ScaffoldVariables {
  projectName: string;
  projectDescription: string;
  teamOwner: string;
  avatarVersion: string;
  packVersion: string;
  lastScan: string;
  // v1.4.0 — GitNexus section content (empty string nếu không cài).
  // Mustache engine không support conditional → render pre-built string.
  gitnexusSection: string;
  // Index signature — `renderTemplateByName` consume Record<string, string | number | undefined>.
  // Cho phép template engine truy cập field bằng string key động.
  [key: string]: string | number | undefined;
}

// Create .claude/{project,state,_pending,_backup}/ and a .gitkeep in each
// so they survive a fresh checkout.
export async function createClaudeDirTree(projectRoot: string): Promise<void> {
  const claudeRoot = join(projectRoot, ".claude");
  await ensureDir(claudeRoot);
  for (const sub of CLAUDE_SUBDIRS) {
    const dir = join(claudeRoot, sub);
    await ensureDir(dir);
    await writeTextAtomic(join(dir, ".gitkeep"), "");
  }
}

// Marker constants tách sang workspace-marker-constants.ts (không side-effect) để
// resolver import được mà không kéo template-bundle-loader. Re-export giữ API cũ
// dưới tên mới WORKSPACE_MARKER_RELATIVE.
import { WORKSPACE_MARKER_RELATIVE } from "./workspace-marker-constants.js";

export { WORKSPACE_MARKER_RELATIVE } from "./workspace-marker-constants.js";

// Ghi marker .claude/avatar.json lúc init — định danh đây là Avatar workspace thật.
// `product` cố định "avatar" (phân biệt tường minh); avatarVersion + createdAt để debug.
export async function writeAvatarWorkspaceMarker(
  projectRoot: string,
  data: { avatarVersion: string; workspaceName: string },
): Promise<void> {
  const path = join(projectRoot, WORKSPACE_MARKER_RELATIVE);
  const content = `${JSON.stringify(
    {
      product: "avatar",
      avatarWorkspace: true,
      avatarVersion: data.avatarVersion,
      workspaceName: data.workspaceName,
      createdAt: new Date().toISOString(),
    },
    null,
    2,
  )}\n`;
  await writeTextAtomic(path, content);
}

// v1.3.3-v1.11.1: STUB. Project knowledge files (tech-stack/conventions/architecture/
// domain/gotchas) chờ `avatar scan` (M06) + team-ai-pack content ready.
// Caller `init.ts` vẫn gọi để giữ API stable cho khi re-enable, hiện trả [].
//
// Re-enable checklist:
//   1. team-ai-pack có content thật (chương trình knowledge curation chạy)
//   2. avatar scan implement xong + output structured knowledge JSON
//   3. CLAUDE.md template re-add @-imports cho 5 file knowledge
//   4. Update test/lib/project-tree-scaffolder.test.ts ngừng expect []
//
// Nếu sau v1.15 vẫn chưa wire → đánh giá lại có cần stub không, có thể xóa và
// inline empty array tại caller để giảm noise.
export async function writeProjectKnowledgeFiles(
  _projectRoot: string,
  _vars: ScaffoldVariables,
): Promise<string[]> {
  return [];
}

// Write root CLAUDE.md from template — this is the entry point Claude Code reads.
// Returns backup path if a pre-existing CLAUDE.md was renamed.
export async function writeRootClaudeMd(
  projectRoot: string,
  vars: ScaffoldVariables,
): Promise<string | null> {
  const content = await renderTemplateByName("CLAUDE.md", vars);
  return await writeWithBackup(join(projectRoot, "CLAUDE.md"), content);
}

// `avatar sync` dùng: cập nhật phần Avatar-managed trong CLAUDE.md mà GIỮ
// context user tự thêm. Render template mới (toàn bộ nằm trong marker AVATAR:MANAGED)
// rồi merge vào CLAUDE.md hiện có theo marker block (xem merge-claude-md-managed-block).
//
// - File có marker  → thay nội dung trong marker, giữ phần ngoài (user).
// - File chưa marker → THAY HOÀN TOÀN: xóa sạch nội dung cũ, ghi block Avatar mới.
// - File chưa tồn tại → KHÔNG tạo (đó là việc init). Trả outcome 'skipped'.
//
// Trả MergeClaudeMdResult để caller (sync) log đúng việc đã làm.
export interface MergeClaudeMdResult {
  outcome: "replaced-block" | "replaced-whole" | "skipped-no-file";
}

export async function mergeRootClaudeMdManaged(
  projectRoot: string,
  vars: ScaffoldVariables,
): Promise<MergeClaudeMdResult> {
  const claudeMdPath = join(projectRoot, "CLAUDE.md");
  if (!(await pathExists(claudeMdPath))) {
    return { outcome: "skipped-no-file" };
  }
  const current = await fs.readFile(claudeMdPath, "utf8");
  const newManaged = await renderTemplateByName("CLAUDE.md", vars);
  const { mergeClaudeMdManagedBlock } = await import("./merge-claude-md-managed-block.js");
  const merged = mergeClaudeMdManagedBlock(current, newManaged);
  await writeTextAtomic(claudeMdPath, merged.content);
  return { outcome: merged.outcome as MergeClaudeMdResult["outcome"] };
}

// Write .claude/settings.json from template. Returns backup path if existed.
//
// #164 writer #9 (chốt d) — KHÔNG phải RMW mà DESTRUCTIVE: `writeWithBackup` gọi
// `backupIfExists` (fs.rename DỜI file cũ đi) rồi ghi đè từ template, không đọc nội
// dung cũ. `avatar init` chạy song song `avatar sync` vì thế mất TRỌN settings —
// nặng hơn lost update. Lock bao trọn backup+write để `init` không xen vào giữa
// bracket của `sync`. Render template ở NGOÀI lock: nó không đụng file đích.
//
// ⚠️ Đường này chạy khi `.claude/` CÓ THỂ CHƯA TỒN TẠI ⇒ helper lock phải
// `ensureDir(dirname(lockPath))` trước `open('wx')`, nếu không `init` sạch sẽ ENOENT.
// Đã canh bằng case `..._creates_lock_dir_when_claude_dir_missing` ở phase-02.
export async function writeProjectSettings(
  projectRoot: string,
  vars: ScaffoldVariables,
): Promise<string | null> {
  const content = await renderTemplateByName("settings.json", vars);
  return await withSettingsFileLock(projectRoot, () =>
    writeWithBackup(join(projectRoot, ".claude", "settings.json"), content),
  );
}

// v3: workspace root KHÔNG git → KHÔNG tạo .gitignore ở root (gây hiểu nhầm).
// Thay bằng .claude/.gitignore nhỏ — bảo vệ settings.json (chứa API key) + state,
// PHÒNG khi user/Claude lỡ git init bên trong .claude/ hoặc workspace.
// Nội dung path tương đối từ .claude/ (không phải workspace root).
//
// #172: hằng nội dung dời sang `claude-gitignore-required-patterns.ts` (module THUẦN, 0 I/O) vì
// `sync` và `doctor` cũng cần cùng danh sách pattern — để ở đây thì 2 consumer đó phải nhân bản
// literal. `writeClaudeGitignore` giữ nguyên hành vi GHI ĐÈ toàn file (đúng cho `init`).

export async function writeClaudeGitignore(projectRoot: string): Promise<void> {
  const path = join(projectRoot, ".claude", ".gitignore");
  await writeTextAtomic(path, CLAUDE_GITIGNORE_CONTENT);
}
